import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Eye, Calendar } from 'lucide-react';

const BlogCard = ({ post, featured = false }) => {
  const [imageError, setImageError] = useState(false);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const generateTextImage = (title, categoryColor) => {
    const words = title.split(' ');
    const lines = [];
    
    // Group words into lines of 3
    for (let i = 0; i < words.length; i += 3) {
      lines.push(words.slice(i, i + 3).join(' '));
    }
    
    return (
      <div 
        className="w-full h-full flex items-center justify-center text-white font-bold"
        style={{ backgroundColor: categoryColor || '#3B82F6' }}
      >
        <div className="text-center p-4">
          {lines.map((line, index) => (
            <div key={index} className="text-2xl md:text-3xl leading-tight">
              {line}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleImageError = () => {
    setImageError(true);
  };

  if (featured) {
    return (
      <Link to={`/blog/${post.slug}`} className="block group">
        <div className="relative overflow-hidden rounded-2xl shadow-xl hover-lift">
          <div className="aspect-[21/9] relative">
            {!imageError ? (
              <img
                src={post.featured_image}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                onError={handleImageError}
              />
            ) : (
              generateTextImage(post.title, post.category_color)
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <div className="flex items-center space-x-4 mb-4">
              <span
                className="px-3 py-1 rounded-full text-sm font-semibold"
                style={{ backgroundColor: post.category_color }}
              >
                {post.category_name}
              </span>
              {post.is_featured && (
                <span className="px-3 py-1 bg-yellow-500 rounded-full text-sm font-semibold">
                  ⭐ Featured
                </span>
              )}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-3 group-hover:text-blue-300 transition">
              {post.title}
            </h2>
            <p className="text-gray-200 mb-4 line-clamp-2">{post.excerpt}</p>
            <div className="flex items-center space-x-6 text-sm text-gray-300">
              <div className="flex items-center space-x-2">
                <Calendar size={16} />
                <span>{formatDate(post.published_at)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>{post.reading_time} min read</span>
              </div>
              <div className="flex items-center space-x-2">
                <Eye size={16} />
                <span>{post.views.toLocaleString()} views</span>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link to={`/blog/${post.slug}`} className="block group">
      <div className="bg-white rounded-xl shadow-md overflow-hidden hover-lift h-full flex flex-col">
        <div className="aspect-video relative overflow-hidden">
          {!imageError ? (
            <img
              src={post.featured_image}
              alt={post.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              onError={handleImageError}
            />
          ) : (
            generateTextImage(post.title, post.category_color)
          )}
          {post.is_featured && (
            <div className="absolute top-4 right-4 bg-yellow-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
              ⭐ Featured
            </div>
          )}
        </div>
        <div className="p-6 flex-1 flex flex-col">
          <div className="mb-3">
            <span
              className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: post.category_color }}
            >
              {post.category_name}
            </span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition line-clamp-2">
            {post.title}
          </h3>
          <p className="text-gray-600 mb-4 line-clamp-3 flex-1">{post.excerpt}</p>
          <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-1">
                <Clock size={14} />
                <span>{post.reading_time} min</span>
              </div>
              <div className="flex items-center space-x-1">
                <Eye size={14} />
                <span>{post.views.toLocaleString()}</span>
              </div>
            </div>
            <span>{formatDate(post.published_at)}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BlogCard;
